import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, FileText, User as UserIcon, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { getProfile, updateProfile, getApplications } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { DocumentsManager } from "../components/DocumentsManager";

const STATUS_STYLE = {
  new: "bg-[#EAF5FD] text-[#1088D4]",
  "in-progress": "bg-amber-50 text-amber-700",
  approved: "bg-green-50 text-green-700",
  rejected: "bg-red-50 text-red-700",
};

const LOAN_LABEL = { personal: "Personal Loan", business: "Business Loan", overdraft: "Flexi Overdraft" };

const fmtAmt = (n) => (n ? `₹${Number(n).toLocaleString("en-IN")}` : "—");
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); } catch { return "—"; } };

const initials = (name, email) =>
  (name || email || "U").split(" ").map(p => p[0]).slice(0, 2).join("").toUpperCase();

export default function ProfilePage() {
  const { user, setUser, logout } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [apps, setApps] = useState([]);
  const [loadingApps, setLoadingApps] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    getProfile().then((p) => setForm({
      name: p.name || "", mobile: p.mobile || "", city: p.city || "",
      pan: p.pan || "", dob: p.dob || "", employer_name: p.employer_name || "",
      monthly_salary: p.monthly_salary || "", employment_type: p.employment_type || "salaried",
    })).catch(() => setLoadError(true));
    getApplications().then(setApps).catch(() => setApps([])).finally(() => setLoadingApps(false));
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const onSave = async (e) => {
    e.preventDefault();
    if (form.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.pan.toUpperCase())) {
      toast.error("Enter a valid PAN (e.g. ABCDE1234F)");
      return;
    }
    if (form.mobile && !/^[6-9]\d{9}$/.test(form.mobile)) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        pan: form.pan ? form.pan.toUpperCase() : null,
        monthly_salary: form.monthly_salary ? Number(form.monthly_salary) : null,
      };
      const updated = await updateProfile(payload);
      setUser(updated);
      toast.success("Profile updated");
    } catch {
      toast.error("Could not update profile");
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    if (loadError) {
      return (
        <div className="ol-container py-24 text-center" data-testid="profile-load-error">
          <p className="text-[#273B59] font-semibold">Could not load your profile.</p>
          <Button onClick={() => window.location.reload()} className="mt-4 ol-btn-primary">Retry</Button>
        </div>
      );
    }
    return <div className="ol-container py-24 flex justify-center"><Loader2 className="animate-spin text-[#1088D4]" size={32} /></div>;
  }

  return (
    <div data-testid="profile-page" className="ol-container py-10">
      <Breadcrumbs items={[{ label: "My Account" }]} />

      <div className="mt-6 flex items-center gap-4">
        <Avatar className="w-16 h-16 border border-[#E2E8F0]">
          <AvatarImage src={user?.picture} alt={user?.name} />
          <AvatarFallback className="bg-[#273B59] text-white text-lg">{initials(user?.name, user?.email)}</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <h1 data-testid="profile-name" className="text-2xl font-bold font-heading text-[#273B59]">{user?.name || "Welcome"}</h1>
          <p data-testid="profile-email" className="text-sm text-[#475569]">{user?.email}</p>
        </div>
        <Button data-testid="profile-logout-btn" variant="outline" onClick={logout} className="border-[#E2E8F0] text-[#475569]">
          <LogOut size={16} className="mr-2" /> Logout
        </Button>
      </div>

      <Tabs defaultValue="profile" className="mt-8">
        <TabsList data-testid="profile-tabs">
          <TabsTrigger data-testid="tab-profile" value="profile"><UserIcon size={15} className="mr-2" />Profile & KYC</TabsTrigger>
          <TabsTrigger data-testid="tab-applications" value="applications"><FileText size={15} className="mr-2" />My Applications</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <form data-testid="profile-form" onSubmit={onSave} className="mt-6 bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-8 max-w-3xl">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full Name"><Input data-testid="pf-name" value={form.name} onChange={upd("name")} placeholder="Your full name" /></Field>
              <Field label="Mobile Number"><Input data-testid="pf-mobile" value={form.mobile} onChange={upd("mobile")} maxLength={10} placeholder="10-digit mobile" /></Field>
              <Field label="PAN Number"><Input data-testid="pf-pan" value={form.pan} onChange={upd("pan")} maxLength={10} placeholder="ABCDE1234F" className="uppercase" /></Field>
              <Field label="Date of Birth"><Input data-testid="pf-dob" type="date" value={form.dob} onChange={upd("dob")} /></Field>
              <Field label="City"><Input data-testid="pf-city" value={form.city} onChange={upd("city")} placeholder="e.g. Mumbai" /></Field>
              <Field label="Employment Type">
                <Select value={form.employment_type} onValueChange={upd("employment_type")}>
                  <SelectTrigger data-testid="pf-employment" className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="salaried">Salaried</SelectItem>
                    <SelectItem value="self-employed">Self-Employed</SelectItem>
                    <SelectItem value="business">Business Owner</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Employer / Business Name"><Input data-testid="pf-employer" value={form.employer_name} onChange={upd("employer_name")} placeholder="Company name" /></Field>
              <Field label="Monthly Income (₹)"><Input data-testid="pf-salary" type="number" value={form.monthly_salary} onChange={upd("monthly_salary")} placeholder="e.g. 50000" /></Field>
            </div>
            <Button data-testid="profile-save-btn" type="submit" disabled={saving} className="mt-7 ol-btn-primary h-11">
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </form>

          <DocumentsManager />
        </TabsContent>

        <TabsContent value="applications">
          <div className="mt-6 max-w-4xl" data-testid="applications-list">
            <ApplicationsList loading={loadingApps} apps={apps} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs font-medium text-[#475569] mb-1.5 block">{label}</Label>
    {children}
  </div>
);

const ApplicationsList = ({ loading, apps }) => {
  if (loading) {
    return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#1088D4]" size={28} /></div>;
  }
  if (apps.length === 0) {
    return (
      <div data-testid="apps-empty" className="bg-white border border-[#E2E8F0] rounded-2xl p-10 text-center">
        <FileText className="mx-auto text-[#CBD5E1]" size={40} />
        <p className="mt-3 text-[#273B59] font-semibold">No applications yet</p>
        <p className="mt-1 text-sm text-[#475569]">Your loan applications will appear here once you apply.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {apps.map((a) => (
        <div key={a.id} data-testid={`app-card-${a.id}`} className="bg-white border border-[#E2E8F0] rounded-xl p-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-[#273B59]">{LOAN_LABEL[a.loan_type] || a.loan_type}</p>
            <p className="text-sm text-[#475569] mt-0.5">{fmtAmt(a.loan_amount)} · {a.city || "—"} · {fmtDate(a.created_at)}</p>
            {a.lender && <p className="text-xs text-[#94A3B8] mt-0.5">Lender: {a.lender}</p>}
          </div>
          <span className={`text-xs font-semibold px-3 py-1 rounded-full capitalize ${STATUS_STYLE[a.status] || "bg-slate-100 text-slate-600"}`}>{a.status}</span>
        </div>
      ))}
    </div>
  );
};
